import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, BookOpen } from "lucide-react";
import { useContent } from "../contexts/ContentContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useConsultNav } from "../lib/consultSource";

function useAttorneysFor(area) {
  const { attorneys } = useContent();
  if (!area) return [];
  return (area.attorneys || [])
    .map((m) => {
      const a = attorneys.find((x) => x.id === m.attorneyId);
      return a ? { ...a, blurb: m.blurb } : null;
    })
    .filter(Boolean);
}

function AttorneyRow({ attorney, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(attorney.id)}
      className="group w-full text-left flex gap-5 p-5 bg-white border border-stone-200 hover:border-[#b8924a] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#b8924a]/40 transition-all"
      aria-label={`View ${attorney.name}'s bio`}
    >
      <img
        src={attorney.image}
        alt={attorney.name}
        className="w-24 h-28 md:w-28 md:h-32 object-cover shrink-0"
        style={{ objectPosition: attorney.imagePosition || "center top" }}
        loading="lazy"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-serif text-2xl text-[#1a2c4e] leading-tight group-hover:text-[#b8924a] transition-colors">
              {attorney.name}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#b8924a] mt-1.5">
              {attorney.title}
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 text-[#4a6d8c] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
        </div>
        <p className="text-[15px] text-[#3a4d6b] mt-3 leading-relaxed">{attorney.blurb}</p>
        <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#b8924a] opacity-0 group-hover:opacity-100 transition-opacity">
          View full bio &rarr;
        </div>
      </div>
    </button>
  );
}

export default function PracticeAreaDialog({ area, open, onClose, onAttorneyClick }) {
  const attorneys = useAttorneysFor(area);
  const goToConsult = useConsultNav();
  const scheduleConsult = () => {
    onClose();
    goToConsult(`${area.title} — practice area pop-out`, area.id);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 bg-[#fbf9f6] border-stone-200">
        {area && (
          <div className="p-8 md:p-12">
            <DialogHeader>
              <DialogTitle asChild>
                <div>
                  <div className="text-sm uppercase tracking-[0.3em] text-[#b8924a]">Practice Area</div>
                  <h3 className="font-serif text-5xl text-[#1a2c4e] mt-3 mb-3 leading-tight">{area.title}</h3>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="h-px w-16 bg-[#b8924a] mb-6" />
            <p className="text-[#3a4d6b] mb-6 leading-relaxed text-lg">{area.description}</p>
            <Link
              to={`/practice-areas/${area.id}`}
              onClick={onClose}
              className="inline-flex items-center gap-2 text-[#b8924a] hover:text-[#1a2c4e] uppercase tracking-[0.2em] text-xs font-medium mb-10 transition-colors"
              data-testid="practice-dialog-learn-more"
            >
              <BookOpen className="w-4 h-4" /> Learn more about our {area.title} practice <ArrowRight className="w-4 h-4" />
            </Link>

            <div className="text-sm uppercase tracking-[0.2em] text-[#1a2c4e] font-semibold mb-5">
              Attorneys Handling {area.title}
            </div>

            <div className="space-y-5">
              {attorneys.length === 0 ? (
                <p className="text-base text-[#4a6d8c]">
                  Please contact our office for more information about attorneys handling this practice area.
                </p>
              ) : (
                attorneys.map((a) => (
                  <AttorneyRow key={a.id} attorney={a} onSelect={onAttorneyClick} />
                ))
              )}
            </div>

            <div className="mt-10 pt-8 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-5">
              <p className="font-serif text-xl md:text-2xl text-[#1a2c4e] text-center sm:text-left">
                Ready to discuss your matter?
              </p>
              <button
                type="button"
                onClick={scheduleConsult}
                className="inline-flex items-center justify-center h-12 px-8 bg-[#1a2c4e] hover:bg-[#11203c] text-white uppercase tracking-[0.2em] text-xs font-medium border border-[#1a2c4e] transition-colors whitespace-nowrap"
                data-testid="practice-dialog-consult-button"
              >
                Schedule a Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
